import { useState } from "react";
import { motion } from "framer-motion";
import { Check, AlertTriangle, Plus, Gift, Lock } from "lucide-react";
import { toast } from "sonner";
import { useTrip } from "@/lib/store";
import { SubHeader } from "../More";

export default function Organiser() {
  const { trip, organiser, setOrganiser, toggleTask, addTask, updateNotes } = useTrip();
  const [newTask, setNewTask] = useState("");
  const [notes, setNotes] = useState(trip.notes.organiserNotes || "");
  const [msg, setMsg] = useState(trip.notes.birthdayMessage || "");
  const [surprise, setSurprise] = useState(trip.notes.surpriseItem || "");
  const groups = [...new Set(trip.tasks.map((t) => t.group))];
  const unpaid = trip.hotels.filter((h) => h.paymentStatus === "unpaid");
  const done = trip.tasks.filter((t) => t.done).length;

  if (!organiser) return (
    <div className="pb-8"><SubHeader eyebrow="Organiser" title="Organiser view is off" />
      <button data-testid="enable-organiser-btn" onClick={() => setOrganiser(true)} className="tap mt-4 rounded-full bg-[var(--plum)] px-5 py-3 text-sm font-semibold text-white">Turn on Organiser view</button></div>
  );

  return (
    <div className="pb-8">
      <SubHeader eyebrow="Organiser" title="Angelina's checklist" />
      {unpaid.map((h) => (
        <div key={h.id} data-testid="organiser-payment-alert" className="mt-4 flex items-start gap-3 rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 text-rose-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div><p className="eyebrow !text-rose-700">Payment action required</p><p className="font-semibold">{h.city} hotel — {h.name} has NOT been paid according to the spreadsheet.</p></div>
        </div>
      ))}
      <div className="mt-4 flex items-center justify-between text-sm"><p className="text-[var(--ink-muted)]">{done} of {trip.tasks.length} complete</p>
        <div className="h-2 w-40 overflow-hidden rounded-full bg-[var(--parchment-2)]"><motion.div className="h-full bg-[var(--jade)]" animate={{ width: `${(done / Math.max(trip.tasks.length, 1)) * 100}%` }} /></div></div>

      <div data-testid="organiser-checklist" className="mt-3 space-y-4">
        {groups.map((g) => (
          <section key={g} className="card-soft p-3">
            <p className="eyebrow px-1 pb-1">{g}</p>
            <ul>
              {trip.tasks.filter((t) => t.group === g).map((t) => (
                <li key={t.id}>
                  <button data-testid={`task-${t.id}`} onClick={() => { toggleTask(t.id); if (!t.done) toast.success("Nice — ticked off"); }} className="tap flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left hover:bg-[var(--parchment-2)]">
                    <motion.span animate={{ scale: t.done ? [1, 1.25, 1] : 1 }} className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${t.done ? "border-[var(--jade)] bg-[var(--jade)] text-white" : t.urgent ? "border-rose-400" : "border-stone-300"}`}>{t.done && <Check className="h-3.5 w-3.5" />}</motion.span>
                    <span className={`text-sm ${t.done ? "line-through text-[var(--ink-muted)]" : t.urgent ? "font-semibold text-rose-800" : ""}`}>{t.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (!newTask.trim()) return; addTask({ label: newTask.trim(), group: "Other" }); setNewTask(""); }}>
          <input data-testid="new-task-input" value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="Add a task…" className="flex-1 rounded-full border bg-white px-4 py-2.5 text-sm" />
          <button data-testid="new-task-add" type="submit" className="tap rounded-full bg-[var(--plum)] px-4 text-white"><Plus className="h-4 w-4" /></button>
        </form>
      </div>

      <section className="card-soft mt-6 p-4">
        <p className="eyebrow flex items-center gap-1"><Lock className="h-3 w-3" /> Private organiser notes</p>
        <textarea data-testid="organiser-notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Booking references, reminders, anything private…" className="mt-2 w-full rounded-xl border bg-[var(--parchment)] p-3 text-sm" />
        <button data-testid="organiser-notes-save" onClick={() => { updateNotes({ organiserNotes: notes }); toast.success("Notes saved"); }} className="tap mt-2 rounded-full bg-[var(--jade)] px-4 py-2 text-xs font-semibold text-[var(--parchment)]">Save notes</button>
      </section>

      <section className="card-soft mt-4 p-4">
        <p className="eyebrow flex items-center gap-1"><Gift className="h-3 w-3" /> Birthday touches</p>
        <p className="mt-1 text-xs text-[var(--ink-muted)]">Mary's birthday date hasn't been supplied, so nothing is assigned to a day automatically. Mark a "Birthday moment" on any itinerary item from its Edit menu.</p>
        <label className="mt-3 block text-xs"><span className="eyebrow">Message shown to Mary on the home screen</span><textarea data-testid="birthday-message-input" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1 w-full rounded-xl border bg-[var(--parchment)] p-3 text-sm" /></label>
        <label className="mt-3 block text-xs"><span className="eyebrow">Private surprise (organiser only)</span><input data-testid="surprise-input" value={surprise} onChange={(e) => setSurprise(e.target.value)} className="mt-1 w-full rounded-xl border bg-[var(--parchment)] p-3 text-sm" /></label>
        <button data-testid="birthday-save" onClick={() => { updateNotes({ birthdayMessage: msg, surpriseItem: surprise }); toast.success("Saved"); }} className="tap mt-3 rounded-full bg-[var(--jade)] px-4 py-2 text-xs font-semibold text-[var(--parchment)]">Save</button>
      </section>
    </div>
  );
}
